import {
  WHOLESALE_METHOD_LABELS,
  WHOLESALE_PAYMENT_METHODS,
  WHOLESALE_STATUS_LABELS,
  type DriverBundleDTO,
  type WholesalePaymentMethod,
  type WholesaleRouteDTO,
  type WholesaleRouteStopDTO,
} from '@bifeai/shared';
import { Badge, BrandMark, Button, Callout, Dialog, EmptyState, Field, Input, Select, Spinner, useToast } from '@bifeai/ui';
import { ArrowLeft, Check, CloudOff, MapPin, MessageCircle, Phone, RefreshCw, ShoppingBag, Truck, UserPlus, Wallet, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { discardOp, enqueue, opLabel, refreshBundle, retryOp, startDriver, syncOps, useDriver, withOps } from '../../lib/driver';
import { formatMoney, formatDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { STATUS_TONES, lineTotal, moneyInput, parseMoney, parseQty, qtyText } from './common';
import { RouteMapBlock } from './RoutesPage';
import './driver.css';

const uuid = () => crypto.randomUUID();
type Customer = DriverBundleDTO['customers'][number];

/** Pantalla del repartidor en el celular: paradas, entregas, cobros y venta en la calle, con o sin señal. */
export function DriverPage() {
  const { me } = useSession();
  const d = useDriver();
  const [routeId, setRouteId] = useState<string | null>(null);
  const [tab, setTab] = useState<'stops' | 'map' | 'load' | 'summary'>('stops');
  const [paying, setPaying] = useState<{ customerId: string; orderId: string | null; dueCents: number | null } | null>(null);
  const [selling, setSelling] = useState<{ customerId: string | null } | null>(null);
  const [newCustomer, setNewCustomer] = useState(false);
  useEffect(() => startDriver(me.user.id), [me.user.id]);

  const view = useMemo(() => (d.bundle ? withOps(d.bundle, d.ops) : null), [d.bundle, d.ops]);
  const route = view?.routes.find((r) => r.id === routeId) ?? view?.routes.find((r) => r.status === 'ON_ROUTE') ?? view?.routes[0];
  const onRoute = route?.status === 'ON_ROUTE';

  return (
    <div className="bf-drv">
      <header className="bf-drv-top">
        <Link to="/" className="bf-drv-back" aria-label="Volver al sistema">
          <ArrowLeft size={18} aria-hidden />
        </Link>
        <BrandMark className="bf-drv-logo" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bf-drv-title">Reparto</div>
          <div className="bf-drv-sub">{me.user.name}</div>
        </div>
        <button type="button" className={`bf-drv-net${d.online ? '' : ' is-off'}`} onClick={() => void syncOps()} aria-label="Sincronizar">
          {d.online ? (
            <RefreshCw size={14} aria-hidden className={d.syncing || d.loading ? 'bf-spin' : undefined} />
          ) : (
            <CloudOff size={14} aria-hidden />
          )}
          {d.online ? (d.pending ? `Subiendo ${d.pending}` : 'Al día') : d.pending ? `Sin señal · ${d.pending} por subir` : 'Sin señal'}
        </button>
      </header>

      {!view ? (
        <div className="bf-drv-center">
          {d.loading ? (
            <Spinner label="Bajando lo del día" />
          ) : (
            <EmptyState icon={<CloudOff />} title="Sin señal" text="Abrí esta pantalla una vez con internet para bajar tus salidas." />
          )}
        </div>
      ) : !route ? (
        <div className="bf-drv-center">
          <EmptyState
            icon={<Truck />}
            title="No tenés salidas abiertas"
            text="Cuando te armen una hoja de ruta, aparece acá."
            action={<Button onClick={() => void refreshBundle()}>Actualizar</Button>}
          />
        </div>
      ) : (
        <>
          {view.routes.length > 1 && (
            <div className="bf-drv-chips">
              {view.routes.map((r) => (
                <button key={r.id} type="button" aria-pressed={r.id === route.id} onClick={() => setRouteId(r.id)}>
                  #{r.number} · {formatDate(r.date)}
                </button>
              ))}
            </div>
          )}
          <section className="bf-drv-route">
            <div>
              <strong>Salida #{route.number}</strong> · {formatDate(route.date)}
              {route.vehicleName && <span className="bf-drv-sub"> · {route.vehicleName}</span>}
            </div>
            <Progress r={route} />
            {route.notes && <div className="bf-drv-sub">{route.notes}</div>}
            {route.status === 'PLANNED' && <StartRoute r={route} online={d.online} />}
          </section>

          {d.failed.length > 0 && (
            <Callout tone="danger">
              {d.failed.length === 1 ? 'Una cosa no se pudo guardar' : `${d.failed.length} cosas no se pudieron guardar`}. Mirá en Resumen.
            </Callout>
          )}

          <nav className="bf-drv-tabs" aria-label="Secciones">
            {(
              [
                ['stops', 'Paradas'],
                ['map', 'Mapa'],
                ['load', 'Carga'],
                ['summary', 'Resumen'],
              ] as const
            ).map(([k, label]) => (
              <button key={k} type="button" aria-current={tab === k} onClick={() => setTab(k)}>
                {label}
              </button>
            ))}
          </nav>

          {tab === 'stops' && (
            <div className="bf-drv-list">
              {route.stops.map((s) => (
                <StopCard
                  key={s.id}
                  s={s}
                  canAct={onRoute}
                  canSell={onRoute && route.load.some((l) => l.qtyAvailable > 0)}
                  onPay={() => setPaying({ customerId: s.customerId, orderId: s.orderId, dueCents: s.orderDueCents })}
                  onSell={() => setSelling({ customerId: s.customerId })}
                />
              ))}
              {onRoute && (
                <div className="bf-drv-extra">
                  {route.load.some((l) => l.qtyAvailable > 0) && (
                    <Button icon={<ShoppingBag size={16} />} onClick={() => setSelling({ customerId: null })}>
                      Venta a otro cliente
                    </Button>
                  )}
                  <Button icon={<UserPlus size={16} />} onClick={() => setNewCustomer(true)}>
                    Cliente nuevo
                  </Button>
                  <Button icon={<Wallet size={16} />} onClick={() => setPaying({ customerId: '', orderId: null, dueCents: null })}>
                    Cobro a cuenta
                  </Button>
                </div>
              )}
            </div>
          )}

          {tab === 'map' &&
            (d.online ? (
              <RouteMapBlock r={route} onlyPending />
            ) : (
              <div className="bf-drv-center bf-drv-sub">El mapa necesita señal. Las paradas y todo lo demás siguen andando.</div>
            ))}
          {tab === 'load' && <LoadTab r={route} />}
          {tab === 'summary' && <SummaryTab r={route} />}
        </>
      )}

      {paying && view && route && <PaySheet bundle={view} {...paying} onClose={() => setPaying(null)} />}
      {selling && view && route && <SellSheet bundle={view} route={route} customerId={selling.customerId} onClose={() => setSelling(null)} />}
      {newCustomer && <NewCustomerSheet onClose={() => setNewCustomer(false)} />}
    </div>
  );
}

function Progress({ r }: { r: WholesaleRouteDTO }) {
  const done = r.stops.filter((s) => s.status !== 'PENDING').length;
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div className="bf-ws-route-progress">
        <span style={{ width: `${r.stops.length ? (done / r.stops.length) * 100 : 0}%` }} />
      </div>
      <span className="bf-drv-sub">
        {done} de {r.stops.length} paradas
      </span>
    </div>
  );
}

function StartRoute({ r, online }: { r: WholesaleRouteDTO; online: boolean }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Callout tone="warning">
      <div style={{ display: 'grid', gap: 8 }}>
        La salida todavía no arrancó. Cuando cargues la camioneta, tocá “Salgo”.
        <Button
          variant="primary"
          loading={busy}
          disabled={!online}
          onClick={async () => {
            setBusy(true);
            try {
              await api.post(`/wholesale/routes/${r.id}/start`);
              await refreshBundle();
            } catch (e) {
              toast.error('No se pudo arrancar', errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {online ? 'Salgo' : 'Necesita señal para arrancar'}
        </Button>
      </div>
    </Callout>
  );
}

function StopCard({
  s,
  canAct,
  canSell,
  onPay,
  onSell,
}: {
  s: WholesaleRouteStopDTO;
  canAct: boolean;
  canSell: boolean;
  onPay: () => void;
  onSell: () => void;
}) {
  const delivered = s.orderStatus === 'DELIVERED' || s.orderStatus === 'SETTLED';
  const tel = s.phone?.replace(/[^\d+]/g, '');
  const stop = (status: 'DONE' | 'SKIPPED' | 'PENDING', note?: string) =>
    enqueue({ id: uuid(), type: 'STOP', stopId: s.id, status, note: note ?? null });
  return (
    <article className={`bf-drv-stop is-${s.status.toLowerCase()}`}>
      <div className="bf-drv-stop-head">
        <span className="bf-ws-stop-n">{s.position}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bf-drv-stop-name">{s.customerName}</div>
          {s.address && (
            <a
              className="bf-drv-sub"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.lat !== null && s.lng !== null ? `${s.lat},${s.lng}` : s.address)}`}
              target="_blank"
              rel="noreferrer"
            >
              <MapPin size={12} aria-hidden /> {s.address}
            </a>
          )}
        </div>
        {s.status !== 'PENDING' && <Badge tone={s.status === 'DONE' ? 'success' : 'warning'}>{s.status === 'DONE' ? 'Hecha' : 'No se pudo'}</Badge>}
      </div>
      {s.deliveryNotes && <div className="bf-drv-sub">{s.deliveryNotes}</div>}
      {s.orderNumber ? (
        <div className="bf-drv-order">
          <span>
            Pedido #{s.orderNumber} · {formatMoney(s.orderTotalCents ?? 0)}
          </span>
          {s.orderStatus && <Badge tone={STATUS_TONES[s.orderStatus]}>{WHOLESALE_STATUS_LABELS[s.orderStatus]}</Badge>}
          {!!s.orderDueCents && s.orderDueCents > 0 && <strong className="bf-drv-due">Cobrar {formatMoney(s.orderDueCents)}</strong>}
        </div>
      ) : (
        <div className="bf-drv-sub">Visita (sin pedido)</div>
      )}
      {s.note && <div className="bf-drv-sub">“{s.note}”</div>}
      <div className="bf-drv-actions">
        {tel && (
          <>
            <a className="bf-btn bf-btn--sm" href={`tel:${tel}`} aria-label="Llamar">
              <Phone size={14} aria-hidden />
            </a>
            <a className="bf-btn bf-btn--sm" href={`https://wa.me/${tel.replace('+', '')}`} target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <MessageCircle size={14} aria-hidden />
            </a>
          </>
        )}
        {canAct && s.status === 'PENDING' && (
          <>
            {s.orderId && !delivered ? (
              <Button
                size="sm"
                variant="primary"
                icon={<Check size={14} />}
                onClick={() => {
                  enqueue({ id: uuid(), type: 'DELIVER', orderId: s.orderId! });
                  stop('DONE');
                }}
              >
                Entregué
              </Button>
            ) : (
              <Button size="sm" variant="primary" icon={<Check size={14} />} onClick={() => stop('DONE')}>
                Visitado
              </Button>
            )}
            <Button
              size="sm"
              icon={<X size={14} />}
              onClick={() => {
                const note = window.prompt('¿Qué pasó? (opcional)', 'No estaba');
                if (note !== null) stop('SKIPPED', note);
              }}
            >
              No se pudo
            </Button>
          </>
        )}
        {canAct && (
          <Button size="sm" icon={<Wallet size={14} />} onClick={onPay}>
            Cobrar
          </Button>
        )}
        {canSell && (
          <Button size="sm" icon={<ShoppingBag size={14} />} onClick={onSell}>
            Vender
          </Button>
        )}
        {canAct && s.status !== 'PENDING' && (
          <Button size="sm" variant="ghost" onClick={() => stop('PENDING')}>
            Deshacer
          </Button>
        )}
      </div>
    </article>
  );
}

function LoadTab({ r }: { r: WholesaleRouteDTO }) {
  if (!r.load.length) return <div className="bf-drv-center bf-drv-sub">Esta salida no lleva carga para vender en la calle.</div>;
  return (
    <div className="bf-drv-list">
      {r.load.map((l) => (
        <div key={l.productId} className="bf-drv-load">
          <strong>{l.productName}</strong>
          <div className="bf-drv-load-nums">
            <span>
              Salió <b>{qtyText(l.unitType, l.qtyLoaded)}</b>
            </span>
            <span>
              Vendí <b>{qtyText(l.unitType, l.qtySold)}</b>
            </span>
            <span>
              Queda <b>{qtyText(l.unitType, l.qtyAvailable)}</b>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryTab({ r }: { r: WholesaleRouteDTO }) {
  const d = useDriver();
  const pend = d.ops.filter((o) => !o.synced);
  return (
    <div className="bf-drv-list">
      <div className="bf-drv-kpis">
        <div>
          <span className="bf-drv-sub">Efectivo en mano</span>
          <strong>{formatMoney(r.collected.unsettledCashCents)}</strong>
        </div>
        <div>
          <span className="bf-drv-sub">Otros medios</span>
          <strong>{formatMoney(r.collected.otherCents)}</strong>
        </div>
        <div>
          <span className="bf-drv-sub">Ventas en la calle</span>
          <strong>{formatMoney(r.spotSales.reduce((t, s) => t + s.totalCents, 0))}</strong>
        </div>
      </div>
      <div className="bf-drv-sub">Al volver, rendí el efectivo en la oficina. La carga que sobra se cuenta al cerrar la salida.</div>
      <strong>Por subir</strong>
      {pend.length === 0 ? (
        <div className="bf-drv-sub">
          Todo subido{d.lastSync ? ` (${new Date(d.lastSync).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })})` : ''}.
        </div>
      ) : (
        pend.map((o) => (
          <div key={o.id} className="bf-drv-op">
            <div style={{ flex: 1 }}>
              <div>{opLabel(o, d.bundle)}</div>
              {o.error ? <div className="bf-drv-err">{o.error}</div> : <div className="bf-drv-sub">Esperando señal</div>}
            </div>
            {o.error && (
              <>
                <Button size="sm" onClick={() => retryOp(o.id)}>
                  Reintentar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => discardOp(o.id)}>
                  Descartar
                </Button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------- hojas (cobrar, vender, cliente nuevo)
function CustomerPicker({ customers, value, onChange }: { customers: Customer[]; value: string; onChange: (id: string) => void }) {
  const [q, setQ] = useState('');
  const chosen = customers.find((c) => c.id === value);
  if (chosen)
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <strong style={{ flex: 1 }}>{chosen.name}</strong>
        <Button size="sm" variant="ghost" onClick={() => onChange('')}>
          Cambiar
        </Button>
      </div>
    );
  const t = q.trim().toLowerCase();
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <Input placeholder="Buscar cliente" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar cliente" />
      {customers
        .filter((c) => !t || c.name.toLowerCase().includes(t))
        .slice(0, 6)
        .map((c) => (
          <button key={c.id} type="button" className="bf-pick" onClick={() => onChange(c.id)}>
            <strong>{c.name}</strong>
            {c.balanceCents > 0 && <span className="bf-cell-sub">Debe {formatMoney(c.balanceCents)}</span>}
          </button>
        ))}
    </div>
  );
}

function PaySheet({
  bundle,
  customerId: initial,
  orderId,
  dueCents,
  onClose,
}: {
  bundle: DriverBundleDTO;
  customerId: string;
  orderId: string | null;
  dueCents: number | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [customerId, setCustomerId] = useState(initial);
  const c = bundle.customers.find((x) => x.id === customerId);
  const [method, setMethod] = useState<WholesalePaymentMethod>('CASH');
  const [amount, setAmount] = useState(moneyInput(dueCents ?? (c && c.balanceCents > 0 ? c.balanceCents : null)));
  const [reference, setReference] = useState('');
  const cents = parseMoney(amount);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={orderId ? 'Cobrar el pedido' : 'Cobro a cuenta'}>
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Cliente">
          {initial ? <strong>{c?.name}</strong> : <CustomerPicker customers={bundle.customers} value={customerId} onChange={setCustomerId} />}
        </Field>
        {c && <div className="bf-drv-sub">Saldo de su cuenta: {formatMoney(c.balanceCents)}</div>}
        <Field label="Medio">
          <Select value={method} onChange={(e) => setMethod(e.target.value as WholesalePaymentMethod)}>
            {WHOLESALE_PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {WHOLESALE_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Importe">
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus />
        </Field>
        {method !== 'CASH' && (
          <Field label="Referencia (opcional)">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="N° de transferencia o cheque" />
          </Field>
        )}
        <Button
          variant="primary"
          block
          disabled={!customerId || !cents || cents <= 0}
          onClick={() => {
            enqueue({ id: uuid(), type: 'PAY', customerId, orderId, method, amountCents: cents!, reference: reference || null });
            toast.success('Cobro anotado', formatMoney(cents!));
            onClose();
          }}
        >
          Anotar cobro
        </Button>
      </div>
    </Dialog>
  );
}

function SellSheet({
  bundle,
  route,
  customerId: initial,
  onClose,
}: {
  bundle: DriverBundleDTO;
  route: WholesaleRouteDTO;
  customerId: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [customerId, setCustomerId] = useState(initial ?? '');
  const c = bundle.customers.find((x) => x.id === customerId);
  const listId = c ? c.priceListId : null;
  const avail = route.load.filter((l) => l.qtyAvailable > 0);
  const priceOf = (productId: string) => bundle.prices.find((p) => p.priceListId === listId && p.productId === productId)?.priceCents ?? null;
  const [qty, setQty] = useState<Record<string, string>>({});
  const [price, setPrice] = useState<Record<string, string>>({});
  const [paid, setPaid] = useState<'' | WholesalePaymentMethod>('CASH');
  const items = avail
    .map((l) => {
      const q = parseQty(l.unitType, qty[l.productId] ?? '');
      const listPrice = priceOf(l.productId);
      const p = listPrice ?? parseMoney(price[l.productId] ?? '');
      return { l, q, p, listPrice };
    })
    .filter((x) => x.q);
  const over = items.some((x) => x.q! > x.l.qtyAvailable);
  const noPrice = items.some((x) => x.p === null);
  const total = items.reduce((t, x) => t + lineTotal(x.l.unitType, x.q!, x.p ?? 0), 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Venta en la calle">
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Cliente">
          {initial ? <strong>{c?.name}</strong> : <CustomerPicker customers={bundle.customers} value={customerId} onChange={setCustomerId} />}
        </Field>
        {customerId &&
          avail.map((l) => {
            const lp = priceOf(l.productId);
            return (
              <div key={l.productId} className="bf-drv-sell-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{l.productName}</strong>
                  <div className="bf-drv-sub">
                    Queda {qtyText(l.unitType, l.qtyAvailable)} ·{' '}
                    {lp !== null ? `${formatMoney(lp)} ${l.unitType === 'WEIGHT' ? 'el kilo' : 'c/u'}` : 'sin precio en su lista'}
                  </div>
                </div>
                {lp === null && (
                  <Input
                    value={price[l.productId] ?? ''}
                    onChange={(e) => setPrice({ ...price, [l.productId]: e.target.value })}
                    inputMode="decimal"
                    placeholder="Precio"
                    aria-label={`Precio de ${l.productName}`}
                    style={{ width: 90 }}
                  />
                )}
                <Input
                  value={qty[l.productId] ?? ''}
                  onChange={(e) => setQty({ ...qty, [l.productId]: e.target.value })}
                  inputMode="decimal"
                  placeholder={l.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                  aria-label={`Cantidad de ${l.productName}`}
                  style={{ width: 80 }}
                />
              </div>
            );
          })}
        {over && <Callout tone="danger">No queda tanto en la camioneta.</Callout>}
        {items.length > 0 && (
          <>
            <div className="bf-drv-total">
              Total <strong>{formatMoney(total)}</strong>
            </div>
            <Field label="¿Te pagó?">
              <Select value={paid} onChange={(e) => setPaid(e.target.value as '' | WholesalePaymentMethod)}>
                <option value="">No, va a su cuenta</option>
                {WHOLESALE_PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    Sí, {WHOLESALE_METHOD_LABELS[m].toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Button
          variant="primary"
          block
          disabled={!customerId || !items.length || over || noPrice}
          onClick={() => {
            const orderId = uuid();
            enqueue({
              id: uuid(),
              type: 'SPOT_SALE',
              orderId,
              routeId: route.id,
              customerId,
              items: items.map((x) => ({ productId: x.l.productId, qty: x.q!, unitPriceCents: x.p! })),
            });
            if (paid) enqueue({ id: uuid(), type: 'PAY', customerId, orderId, method: paid, amountCents: total, reference: null });
            toast.success('Venta anotada', formatMoney(total));
            onClose();
          }}
        >
          Anotar venta
        </Button>
      </div>
    </Dialog>
  );
}

function NewCustomerSheet({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [v, setV] = useState({ name: '', phone: '', address: '', cuit: '' });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Cliente nuevo">
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Nombre del local o razón social">
          <Input value={v.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label="WhatsApp o teléfono">
          <Input value={v.phone} onChange={set('phone')} inputMode="tel" />
        </Field>
        <Field label="Dirección">
          <Input value={v.address} onChange={set('address')} />
        </Field>
        <Field label="CUIT (opcional)">
          <Input value={v.cuit} onChange={set('cuit')} inputMode="numeric" />
        </Field>
        <div className="bf-drv-sub">Queda como cliente tuyo, con la lista general. En la oficina lo revisan y le asignan lista y límite.</div>
        <Button
          variant="primary"
          block
          disabled={v.name.trim().length < 2}
          onClick={() => {
            enqueue({
              id: uuid(),
              type: 'NEW_CUSTOMER',
              customerId: uuid(),
              name: v.name.trim(),
              phone: v.phone || null,
              address: v.address || null,
              cuit: v.cuit || null,
              ivaCondition: 'CF',
            });
            toast.success('Cliente anotado', v.name);
            onClose();
          }}
        >
          Guardar
        </Button>
      </div>
    </Dialog>
  );
}
